import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, XCircle, Loader2, Gamepad2, AlertCircle, RotateCcw, ShieldAlert } from 'lucide-react';
import { Button } from './components/Button';
import { Card } from './components/Card';
import { onAuthStateChanged } from "firebase/auth";
import { auth, analytics } from './firebase';

const API_URL = "https://4v2z67ln22atuzfyvw6rtwmoe40jgrqx.lambda-url.us-east-1.on.aws";

export default function NewProjectWizard() {
  const navigate = useNavigate();

  // UI State
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejectedEarly, setRejectedEarly] = useState(null);
  
  // <--- ADDED: Credit Error State --->
  const [creditError, setCreditError] = useState(null);

  // Data State
  const [description, setDescription] = useState("");
  const [dynamicQuestions, setDynamicQuestions] = useState([]);
  const [formData, setFormData] = useState({});
  const [result, setResult] = useState(null);

  const [userId, setUserId] = useState(null);
  const [idToken, setIdToken] = useState(null);

  // Step Calculation
  const totalSteps = 1 + (dynamicQuestions.length || 0) + 1;

  // --- AUTH LISTENER ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        setIdToken(token);
        // Using real UID is better, but keeping your logic if you need hardcoded dev user
        // setUserId("user_dev_01");
        setUserId(user.uid);
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // --- API HANDLERS ---

  const handleInitialAnalyze = async () => {
    if (!description.trim()) return;
    setLoading(true);
    setError(null);
    setRejectedEarly(null);
    setCreditError(null); // <--- Reset specific error

    try {
      const res = await fetch(`${API_URL}/onboarding/analyze`, {
        method: 'POST',
        headers: {                                               'Content-Type': 'application/json',                    'Authorization': `Bearer ${idToken}`                 },
        body: JSON.stringify({ description: description })
      });
      const data = await res.json();

      // <--- ADDED: Check for credit error --->
      if (data.insufficient) {
          setCreditError(data.error);
          return;
      }

      if (data.rejected) {
        setRejectedEarly(data.reason);
        return;
      }

      if (data.questions) {
        setDynamicQuestions(data.questions);
        setStep(1);
      } else {
        throw new Error("Invalid response");
      }
    } catch (err) {
      setError(`Server Error: Unable to analyze idea. ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeProject = async () => {
    setLoading(true);
    setError(null);
    setCreditError(null); // <--- Reset specific error

    try {
      // 1. CREATE PROJECT DOCS (DB & Storage)
      const res = await fetch(`${API_URL}/onboarding/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          // userId,
          description,
          answers: formData
        })
      });

      const creationData = await res.json(); // Renamed to creationData to avoid confusion

      // <--- ADDED: Check for credit error --->
      if (creationData.insufficient) {
          setCreditError(creationData.error);
          return;
      }

      if (!creationData.success) {
          throw new Error("Project creation failed.");
      }

      // 2. TRIGGER AI INITIALIZATION (Background)
      try {
        console.log("Triggering initialization for:", creationData.projectId);

        await fetch(`${API_URL}/new/project`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
                // CRITICAL FIX: Use creationData from the previous await, NOT 'result' state
                projectId: creationData.projectId,
                userId,
                // Fix: Serialize formData object so it doesn't appear as [object Object]
                description: `${description}. Details: ${JSON.stringify(formData)}`
            })
        });

        console.log("Background init started.");
      } catch (e) {
        console.error("Initialization Trigger Error:", e);
        // We continue even if this fails, as the project exists in DB now
      }

      // 3. UPDATE UI
      setResult(creationData);
      setStep(step + 1);

    } catch (err) {
      console.error(err);
            alert(err)
      setError("Failed to create project blueprint.");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step === 0) {
      handleInitialAnalyze();
      return;
    }
    if (step === dynamicQuestions.length) {
      handleFinalizeProject();
    } else {
      setStep(step + 1);
    }
  };

  const handleRestart = () => {
    setStep(0);
    setResult(null);
    setRejectedEarly(null);
    setCreditError(null); // <--- Reset
    setDynamicQuestions([]);
    setFormData({});
    setDescription("");
  };

  // --- RENDERERS ---

  const renderInput = (question) => {
    const commonClasses = "w-full bg-slate-900 border border-slate-700 rounded-md p-3 text-sm text-white focus:ring-2 focus:ring-blue-600 outline-none transition-all";
    const val = formData[question.id] || '';

    switch(question.type) {
      case 'select':
        return (
          <select
            className={commonClasses}
            onChange={(e) => setFormData({...formData, [question.id]: e.target.value})}
            value={val}
          >
            <option value="" disabled>Select an option</option>
            {question.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      default:
        return (
          <textarea
            className={`${commonClasses} min-h-[80px] resize-none`}
            placeholder={question.placeholder || "Enter details..."}
            onChange={(e) => setFormData({...formData, [question.id]: e.target.value})}
            value={val}
          />
        );
    }
  };

  // Determine Icon
  const getHeaderIcon = () => {
    if (rejectedEarly) return <XCircle size={32} />;
    if (!result) return <Gamepad2 size={32} />;
    return result.success ? <CheckCircle size={32} /> : <XCircle size={32} />;
  };

  const getHeaderColor = () => {
    if (rejectedEarly) return 'bg-red-900/20 border-red-500/30 text-red-400';
    if (!result) return 'bg-blue-900/20 border-blue-500/30 text-blue-400';
    return result.success
      ? 'bg-green-900/20 border-green-500/30 text-green-400'
      : 'bg-red-900/20 border-red-500/30 text-red-400';
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4 font-sans text-slate-200">
      <Card className="w-full max-w-lg relative overflow-hidden border-slate-800 bg-slate-950/80 backdrop-blur-md shadow-2xl">

        {/* --- RESTORED BLUE RIBBON --- */}
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-80 shadow-[0_0_10px_#3b82f6]" />

        {/* Progress Bar */}
        {!rejectedEarly && !result?.success === false && (
            <div className="absolute top-0 left-0 h-[2px] bg-transparent w-full">
            <div
                className="h-full bg-blue-400 shadow-[0_0_8px_#60a5fa] transition-all duration-500 ease-out"
                style={{ width: `${((step + 1) / (totalSteps || 2)) * 100}%` }}
            />
            </div>
        )}

        <div className="p-5 md:p-6">

          {/* Header Icon */}
          <div className="mb-6 flex justify-center">
             <div className={`p-3 rounded-full border ${getHeaderColor()} transition-all duration-500`}>
               {getHeaderIcon()}
             </div>
          </div>
          
          {/* <--- ADDED: Error Banner for Insufficient Credits ---> */}
          {creditError && (
            <div className="mb-6 p-3 bg-red-950/40 border border-red-500/30 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2">
                <ShieldAlert size={18} className="text-red-500 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-red-200">
                    <span className="font-bold text-red-400 block mb-0.5">Payment Required</span>
                    {creditError}
                </div>
            </div>
          )}

          {/* EARLY REJECTION SCREEN */}
          {rejectedEarly && (
            <div className="text-center space-y-4 animate-in zoom-in duration-300">
                <h2 className="text-xl font-bold text-red-400">Concept Rejected</h2>
                <p className="text-slate-400 border-l-2 border-red-900/50 pl-4 italic text-sm">
                    "{rejectedEarly}"
                </p>
                <Button onClick={handleRestart} className="w-full bg-slate-800 hover:bg-slate-700 text-sm">
                    <RotateCcw className="mr-2" size={14} /> Try Different Idea
                </Button>
            </div>
          )}

          {/* STEP 0: Idea Input */}
          {step === 0 && !loading && !rejectedEarly && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="text-center">
                <h2 className="text-xl font-bold text-white tracking-tight">New Project</h2>
                <p className="text-slate-400 mt-1 text-sm">Describe your game along with the name, mechanics, theme, and goals.</p>
              </div>
              <textarea
                className="w-full h-32 bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white focus:ring-2 focus:ring-blue-600 outline-none resize-none placeholder:text-slate-600 shadow-inner"
                placeholder="e.g. A competitive cooking game set in zero gravity called..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              ></textarea>
            </div>
          )}

          {/* STEPS 1..N: Questions */}
          {step > 0 && !result && !loading && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
               <div className="text-center mb-4">
                 <span className="text-[10px] font-bold text-blue-400 tracking-widest uppercase">Refining Concept</span>
                 <h2 className="text-lg font-semibold text-white mt-1 leading-snug">
                   {dynamicQuestions[step - 1]?.label}
                 </h2>
               </div>
               {renderInput(dynamicQuestions[step - 1])}
            </div>
          )}

          {/* LOADING */}
          {loading && (
            <div className="flex flex-col items-center py-10">
              <Loader2 className="animate-spin text-blue-500 mb-4" size={40} />
              <p className="text-slate-300 animate-pulse font-mono text-sm">
                {step === 0 ? "Analyzing Feasibility..." : "Generating Blueprint, this may take a while..."}
              </p>
            </div>
          )}

          {/* FINAL RESULT */}
          {result && (
            <div className="text-center space-y-6 animate-in zoom-in duration-300">

              {/* Thumbnail */}
              <div className={`relative w-full h-44 rounded-lg overflow-hidden border ${result.success ? 'border-slate-700' : 'border-red-900/50 bg-red-950/20'} shadow-lg group`}>
                {result.thumbnail ? (
                   <img src={result.thumbnail} alt="Generated Concept" className="w-full h-full object-cover" />
                ) : (
                   <div className="w-full h-full flex items-center justify-center text-slate-500 font-mono text-xs">
                      {result.success ? "Generating Image..." : "No Image Generated"}
                   </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-90" />
                <h2 className="absolute bottom-3 left-4 text-xl font-bold text-white text-shadow-lg text-left pr-4">
                  {result.title || "Untitled Project"}
                </h2>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-lg">
                  <div className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Feasibility</div>
                  <div className={`text-2xl font-bold mt-1 ${result.success ? 'text-blue-400' : 'text-red-400'}`}>
                    {result.stats.feasibility}%
                  </div>
                </div>
                <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-lg">
                  <div className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">AI Rating</div>
                  <div className={`text-2xl font-bold mt-1 ${result.success ? 'text-yellow-400' : 'text-red-500'}`}>
                    {result.stats.rating}
                  </div>
                </div>
              </div>

              {/* Summary */}
              <p className="text-slate-400 text-xs italic border-l-2 border-slate-700 pl-3 text-left">
                "{result.stats.summary}"
              </p>

              {/* Actions */}
              {result.success ? (
                  <Button
                    className="w-full py-4 text-base bg-blue-600 hover:bg-blue-500 shadow-[0_0_15px_rgba(37,99,235,0.3)] transition-all hover:scale-[1.02]"
                    onClick={() => navigate(`/console/project/${result.projectId}`)}
                  >
                    Launch Workspace <ArrowRight className="ml-2" size={16} />
                  </Button>
              ) : (
                  <Button
                    className="w-full py-4 text-base bg-slate-800 hover:bg-slate-700 border border-slate-700"
                    onClick={handleRestart}
                  >
                    <RotateCcw className="mr-2" size={16} /> Refine Concept & Try Again
                  </Button>
              )}
            </div>
          )}

          {/* Global Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-400 bg-red-950/30 p-3 rounded-lg border border-red-900/50 mt-4 text-sm">
               <AlertCircle size={18} />
               <span className="font-medium">{error}</span>
            </div>
          )}

          {/* Navigation Buttons */}
          {!result && !loading && !rejectedEarly && (
            <div className="flex justify-end mt-6">
              <Button
                onClick={handleNext}
                className="w-full md:w-auto px-6 py-2 bg-blue-600 hover:bg-blue-500 text-sm"
                disabled={!description && step === 0}
              >
                {step === dynamicQuestions.length ? 'Generate Blueprint' : 'Next Step'} <ArrowRight size={14} className="ml-2" />
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
